/**
 * Drawing control — Ubi's actual control surface.
 *
 * > *"I need to be in control so that I can mark … which drawing is available and the
 * > dashboard needs to display all of the orders in the versions that I leave open."*
 * > *"No no no no no … it's just a flag within the application that filters what you want
 * > to show them or not."*
 *
 * ⚠ **The unit of publication is the DRAWING VERSION, never the order row.** `HUY-1A-B003`
 * is open or hidden and all of its orders follow. A per-order flag would have nowhere to
 * live, and worse, it would be destroyed by Ubi's own housekeeping: re-uploading a drawing
 * moves its rows from `B003` to `B004`, which is exactly why the snapshot row key excludes
 * the drawing name (hard rule 4). Publication has to be keyed on the thing she actually
 * acts on.
 *
 * ⚠ **A drawing is published to an AUDIENCE of companies, not to "contractors" as a
 * whole.** Ubi, 17 Sep 2026: *"both subcos at the same time please, because some areas
 * were re-assigned between the two, so there is still a backlog of overlapping orders."*
 * So the audience is a set: empty means hidden, one company behaves exactly as the old
 * boolean did, and two or more means those companies share that drawing's backlog —
 * which is the whole point, because DMP's `contractor` column is stale for the
 * re-assigned areas. `visibleRows` in order-activation.ts is where that is enforced.
 *
 * ⚠ **Merged drawings are GROUPED, and a group is published as ONE unit.** Ubi, feedback
 * of 28 Sep 2026: *"ability to create POP groups based on which drawings have been merged:
 * group name is POPname-B001 … to list all drawings underneath"*. Her examples are chosen
 * by hand — `HUY-1A-B001` heads B002 to B007 but not B008 or B009 — so a group can never
 * be derived from the names, and it is never "every version of the POP". The group is
 * named after its head, lives inside one POP, and has no chains. Every drawing in it holds
 * the head's audience at all times: `planAudienceChange` widens any write to the whole
 * group, so `visibleRows` needs no notion of groups at all and each order keeps its real
 * drawing name in the report.
 */
/** Published to at least one company. The ONLY definition of "open". */
export function isDrawingOpen(drawing) {
    return drawing.openToCompanyIds.length > 0;
}
/**
 * A drawing whose name does not follow `POP-Bnnn`.
 *
 * ⚠ These are the reason the POP is read from `area_pop` and never parsed out of the
 * drawing name (hard rule 2): `TOURN_MODIF` and `JODO_GF` break the convention, and
 * stripping a `-Bnnn` suffix yields 80 POPs where there are 74. Flagged on the screen so
 * the exceptions are visible rather than surprising.
 */
export function isIrregularDrawingName(projectname) {
    return !/-B\d+$/.test(projectname);
}
/**
 * The order versions are read in down a POP.
 *
 * ⚠ Numbered versions in sequence first, the irregular ones after — not one flat
 * alphabetical sort. `_` sorts before `-`, so plain localeCompare puts `WTRL_MODIF` ahead
 * of `WTRL-1A-B001` and breaks the version sequence Ubi reads down the page. The mockup
 * lists the odd ones last for the same reason. Also what decides a group's head: the
 * lowest-numbered drawing selected, which is the `POPname-B001` her feedback names.
 */
export function compareVersions(a, b) {
    const irregular = Number(isIrregularDrawingName(a)) - Number(isIrregularDrawingName(b));
    return irregular !== 0 ? irregular : a.localeCompare(b);
}
/** The card key for drawings DMP gives no POP. Not a POP name, and never shown as one. */
export const NO_POP_KEY = "(no POP)";
/**
 * Every drawing version DMP knows, each with ONE POP: the one most of its connections are in.
 *
 * Hugues, 29 Sep 2026: *"instead of the DMP based pending orders, list them all drawing
 * versions"*. Drawing Control used to learn of a drawing only from an order awaiting
 * activation, so a drawing with none — `WTRL-1A-B002`, on Ubi's own list of merged
 * drawings — could never be seen, let alone grouped.
 *
 * ⚠ Measured the same day: across ALL connections, 12 of 176 drawings span more than one
 * POP. The numbered ones are lopsided (`BRALEU-1A-B001`: 757 in BRALEU-1A, 1 in BRALEU-1B);
 * the `_MODIF` ones genuinely spread (`CHARL_MODIF` touches seven). The POP used to be that
 * of whichever pending order the job met first — arbitrary, and liable to change as orders
 * were activated. The majority over every connection is stable. A tie goes to the POP that
 * sorts first, so the answer never depends on the order rows arrive in.
 *
 * ⚠ `null` only when NO connection has a network row (the five `YUS-1A` drawings on
 * 29 Sep). Hard rule 2: the POP is never parsed out of the name, even when it is missing.
 */
export function drawingPopsFromDmp(rows) {
    const counts = new Map();
    for (const row of rows) {
        if (!row.projectname)
            continue;
        if (!counts.has(row.projectname))
            counts.set(row.projectname, new Map());
        if (row.area_pop === null)
            continue;
        const perPop = counts.get(row.projectname);
        perPop.set(row.area_pop, (perPop.get(row.area_pop) ?? 0) + Number(row.connections));
    }
    const out = new Map();
    for (const [projectname, perPop] of counts) {
        let best = null;
        let bestCount = -1;
        for (const [pop, n] of [...perPop].sort(([a], [b]) => a.localeCompare(b))) {
            if (n > bestCount) {
                best = pop;
                bestCount = n;
            }
        }
        out.set(projectname, best);
    }
    return out;
}
/**
 * Group versions under their POP.
 *
 * ⚠ POPs are listed ALPHABETICALLY. Ubi, feedback of 28 Sep 2026: *"Drawing control -
 * POPs to be listed in alphabetical order"*. They used to be sorted multi-version first,
 * on the theory that the clash cases were the work; she reads the list the way her
 * spreadsheet is sorted, and a POP that moves when its version count changes is a POP she
 * has to hunt for. The "Multi-version" filter still isolates the clash cases.
 *
 * Inside a POP, a group's members follow their head directly (see `orderWithGroups`), so
 * the head reads as the group's name with the merged drawings listed underneath — the
 * layout her feedback draws.
 */
export function groupByPop(drawings) {
    const groups = new Map();
    for (const drawing of drawings) {
        const key = drawing.areaPop ?? NO_POP_KEY;
        let group = groups.get(key);
        if (!group) {
            group = { pop: drawing.areaPop, key, orders: 0, versions: [], openCount: 0, newCount: 0 };
            groups.set(key, group);
        }
        group.versions.push(drawing);
        group.orders += drawing.orders;
        if (isDrawingOpen(drawing))
            group.openCount++;
        if (drawing.isNew)
            group.newCount++;
    }
    for (const group of groups.values()) {
        group.versions = orderWithGroups(group.versions);
    }
    // The drawings with no POP go last: they are real, but they are the exception.
    return [...groups.values()].sort((a, b) => a.pop === null ? 1 : b.pop === null ? -1 : a.pop.localeCompare(b.pop));
}
/**
 * Version order, with each group's members pulled up directly under their head.
 *
 * ⚠ A member whose head is NOT in the list stays in its own place. That happens for real:
 * a filter can show a member without its head, and DMP can move a drawing to another POP
 * after it was grouped. The member is still listed — dropping it would hide a drawing from
 * the one screen that controls it — and the screen says which head it follows.
 */
export function orderWithGroups(versions) {
    const sorted = [...versions].sort((a, b) => compareVersions(a.projectname, b.projectname));
    const present = new Set(sorted.map((v) => v.projectname));
    const membersOf = new Map();
    for (const version of sorted) {
        if (version.groupHead && present.has(version.groupHead)) {
            const list = membersOf.get(version.groupHead) ?? [];
            list.push(version);
            membersOf.set(version.groupHead, list);
        }
    }
    const out = [];
    for (const version of sorted) {
        if (version.groupHead && present.has(version.groupHead))
            continue; // emitted under its head
        out.push(version);
        out.push(...(membersOf.get(version.projectname) ?? []));
    }
    return out;
}
/**
 * How many of a drawing's orders DMP assigns to the holder of `audienceCodes` — which is
 * exactly what opening the drawing to that company (or those companies) opens.
 *
 * ⚠ The SAME rule as `visibleRows`: a contractor's orders are the ones DMP assigns to
 * them, and opening a drawing to two companies opens each one's own and nothing of the
 * other's (26 Oct 2026). An unassigned row is opened to nobody, ever. A contractor also
 * SEES their own rows in a drawing not open to them, flagged Hidden — so this counts
 * open orders, not visible ones.
 */
export function ordersVisibleTo(drawing, audienceCodes) {
    let total = 0;
    for (const [code, count] of Object.entries(drawing.ordersByContractor)) {
        if (audienceCodes.includes(code))
            total += count;
    }
    return total;
}
/**
 * Apply the screen's filters to the grouped list.
 *
 * ⚠ `multi` filters on the POP and keeps every one of its versions; the others filter on
 * the version. A POP is only a clash case as a whole, so showing one version of it would
 * answer a different question than the one the chip asks.
 */
export function filterGroups(groups, filters) {
    const needle = filters.search?.trim().toLowerCase();
    const show = filters.show ?? "all";
    const out = [];
    for (const group of groups) {
        if (show === "multi" && group.versions.length < 2)
            continue;
        const versions = group.versions.filter((version) => {
            if (show === "open" && !isDrawingOpen(version))
                return false;
            if (show === "hidden" && isDrawingOpen(version))
                return false;
            if (show === "new" && !version.isNew)
                return false;
            if (needle) {
                const matches = (group.pop ?? "").toLowerCase().includes(needle) ||
                    version.projectname.toLowerCase().includes(needle);
                if (!matches)
                    return false;
            }
            return true;
        });
        if (versions.length === 0)
            continue;
        out.push({
            ...group,
            versions,
            // ⚠ The header counts stay whole-POP. "3 of 8 open" has to mean three of the POP's
            // eight, not three of the four the current filter happens to show.
        });
    }
    return out;
}
/** The figures on the sticky header. Computed over EVERY drawing, never the filtered view. */
export function summarise(drawings, ordersVisibleToContractors) {
    const pops = new Set();
    let open = 0;
    let neverDecided = 0;
    for (const drawing of drawings) {
        if (drawing.areaPop !== null)
            pops.add(drawing.areaPop);
        if (isDrawingOpen(drawing))
            open++;
        if (drawing.isNew)
            neverDecided++;
    }
    return {
        drawings: drawings.length,
        pops: pops.size,
        open,
        hidden: drawings.length - open,
        neverDecided,
        ordersVisibleToContractors,
    };
}
/** Two audiences holding the same companies, order and duplicates notwithstanding. */
export function sameAudience(a, b) {
    const left = [...new Set(a)].sort();
    const right = [...new Set(b)].sort();
    return left.length === right.length && left.every((id, i) => id === right[i]);
}
/** The audience a drawing should have after applying `mode` with `companyIds`. */
export function nextAudience(current, mode, companyIds) {
    if (mode === "set")
        return [...new Set(companyIds)];
    if (mode === "add")
        return [...new Set([...current, ...companyIds])];
    const removing = new Set(companyIds);
    return [...new Set(current)].filter((id) => !removing.has(id));
}
export function audienceImpact(drawings, companies, mode, companyIds) {
    return impactOfChanges(drawings.map((drawing) => ({
        projectname: drawing.projectname,
        before: drawing.openToCompanyIds,
        after: nextAudience(drawing.openToCompanyIds, mode, companyIds),
        ordersByContractor: drawing.ordersByContractor,
    })), companies);
}
/**
 * The impact of a set of already-planned changes — the core of `audienceImpact`.
 *
 * ⚠ Separate because a group write does not compute each drawing's outcome from its OWN
 * audience: every drawing in a group takes the outcome computed from the HEAD's. Taking
 * the before/after pairs as given is what lets a group write, and creating a group, be
 * stated with the exact same arithmetic as a plain publish (hard rule 24).
 */
export function impactOfChanges(changes, companies) {
    const names = [];
    const gains = {};
    const losses = {};
    for (const change of changes) {
        if (sameAudience(change.before, change.after))
            continue;
        names.push(change.projectname);
        for (const company of companies) {
            // A company's own orders in the drawing, open to them or not. Unassigned orders
            // belong to no company, so they are never in anybody's figure.
            const own = ordersVisibleTo(change, company.dmpCodes);
            const was = change.before.includes(company.id) ? own : 0;
            const now = change.after.includes(company.id) ? own : 0;
            if (now > was)
                gains[company.id] = (gains[company.id] ?? 0) + (now - was);
            else if (now < was)
                losses[company.id] = (losses[company.id] ?? 0) + (was - now);
        }
    }
    return { names, gains, losses };
}
/**
 * Whether a company may be added to a drawing's audience.
 *
 * ⚠ An active CONTRACTOR with at least one DMP contractor code, and nothing else. A
 * company with no code of its own — a homes-passed partner scoped on Monday labels only —
 * has no orders in Order Activation, so opening a drawing to it opens nothing, and a tick
 * that does nothing reads as one that did. Until 26 Oct 2026 it was worse than useless:
 * drawings open to two companies pooled their orders, and a partner published alongside
 * Circet would have seen all of Circet's. `visibleRows` no longer pools, and refuses a
 * company with no code outright; this stays as the write side not inviting the mistake.
 */
export function isAudienceCandidate(company) {
    return company.kind === "CONTRACTOR" && company.isActive && company.dmpContractorCodes.length > 0;
}
/**
 * Why a planned write must not happen, or `null` if it may.
 *
 * ⚠ Judged on what each change ADDS, never on the whole resulting audience. A company that
 * lost its codes after it was published still has to be removable, and a bulk hide must
 * not be refused because of something it is taking away. But nothing — a tick, a bulk
 * publish, a group widening a write to its members, or members adopting their head's
 * audience when a group is created — may put a non-candidate INTO an audience.
 */
export function audienceRefusal(changes, companies) {
    const byId = new Map(companies.map((c) => [c.id, c]));
    for (const change of changes) {
        for (const id of change.after) {
            if (change.before.includes(id))
                continue;
            const company = byId.get(id);
            if (!company)
                return `Not a company a drawing can be opened to: ${id}`;
            if (company.kind !== "CONTRACTOR") {
                return `${company.name} is internal — internal users already see every drawing, so it cannot be an audience.`;
            }
            if (!company.isActive)
                return `${company.name} is deactivated, so no drawing can be opened to it.`;
            if (company.dmpContractorCodes.length === 0) {
                return `${company.name} has no DMP contractor code, so it has no orders in Order Activation — opening a drawing to it would open nothing.`;
            }
        }
    }
    return null;
}
/** Head → its members, in version order. A drawing that heads nothing is absent. */
export function groupMembersOf(drawings) {
    const members = new Map();
    for (const drawing of drawings) {
        if (!drawing.groupHead)
            continue;
        const list = members.get(drawing.groupHead) ?? [];
        list.push(drawing.projectname);
        members.set(drawing.groupHead, list);
    }
    for (const list of members.values())
        list.sort(compareVersions);
    return members;
}
/**
 * Every drawing a write naming `names` actually lands on: each name plus its whole group.
 *
 * ⚠ This is "published as one unit" made structural. The server widens EVERY audience
 * write through it, so a tick on one merged drawing, a bulk action whose filter happens
 * to show only part of a group, or a stale client can never leave a group split between
 * two audiences — which would publish half of what Ubi merged.
 */
export function expandToGroups(names, drawings) {
    const byName = new Map(drawings.map((d) => [d.projectname, d]));
    const members = groupMembersOf(drawings);
    const out = new Set();
    for (const name of names) {
        out.add(name);
        const drawing = byName.get(name);
        if (!drawing)
            continue;
        const head = drawing.groupHead ?? (members.has(name) ? name : null);
        if (!head)
            continue;
        out.add(head);
        for (const member of members.get(head) ?? [])
            out.add(member);
    }
    return [...out];
}
/** The head a new group would get: the lowest-numbered drawing selected. */
export function groupHeadOf(names) {
    return [...names].sort(compareVersions)[0] ?? null;
}
/**
 * Why these drawings cannot be grouped, as a sentence Ubi reads — or `null` if they can.
 *
 * ⚠ An already-grouped drawing is refused rather than moved. Moving it silently would
 * change which audience it follows, and re-heading a group renames it; both deserve a
 * deliberate ungroup first.
 */
export function groupingProblem(selected, all) {
    if (selected.length < 2)
        return "Select at least two drawings to group.";
    const pops = [...new Set(selected.map((d) => d.areaPop ?? "no POP"))].sort();
    if (pops.length > 1) {
        return `A group lives inside one POP — these drawings span ${pops.join(", ")}.`;
    }
    const members = groupMembersOf(all);
    for (const drawing of [...selected].sort((a, b) => compareVersions(a.projectname, b.projectname))) {
        if (drawing.groupHead) {
            return `${drawing.projectname} is already grouped under ${drawing.groupHead} — take it out of that group first.`;
        }
        if (members.has(drawing.projectname)) {
            return `${drawing.projectname} already heads a group — dissolve it first, then group all of them together.`;
        }
    }
    return null;
}
/**
 * What a write actually does to every drawing it lands on, groups widened.
 *
 * ⚠ Inside a group every drawing takes the outcome computed from the HEAD's audience, not
 * its own. They are equal whenever the invariant holds (and the database refuses to commit
 * anything else), so this changes nothing on a healthy board — but it means a write can
 * only ever leave a group whole, never split it further.
 */
export function planAudienceChange(drawings, names, mode, companyIds) {
    const byName = new Map(drawings.map((d) => [d.projectname, d]));
    const unknown = [...new Set(names)].filter((name) => !byName.has(name));
    const known = [...new Set(names)].filter((name) => byName.has(name));
    const expanded = expandToGroups(known, drawings).filter((name) => byName.has(name));
    const asked = new Set(known);
    const members = groupMembersOf(drawings);
    const changes = expanded.map((name) => {
        const drawing = byName.get(name);
        const headName = drawing.groupHead ?? (members.has(name) ? name : null);
        const base = (headName ? byName.get(headName) : undefined)?.openToCompanyIds ?? drawing.openToCompanyIds;
        return {
            drawing,
            before: [...drawing.openToCompanyIds],
            after: nextAudience(base, mode, companyIds).sort(),
        };
    });
    return { changes, widenedBy: expanded.filter((name) => !asked.has(name)), unknown };
}
//# sourceMappingURL=drawings.js.map